import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import addressService from "../../services/addressService";
import logger from "../../utils/logger";

// ==================== ASYNC THUNKS ====================

/**
 * Fetch all addresses for the user
 */
export const fetchAddresses = createAsyncThunk(
  "addresses/fetchAddresses",
  async (_, { rejectWithValue }) => {
    try {
      const addresses = await addressService.getAddresses();
      return addresses;
    } catch (error) {
      logger.error("Error fetching addresses", error);
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch addresses",
      );
    }
  },
);

/**
 * Create a new address
 */
export const createAddress = createAsyncThunk(
  "addresses/createAddress",
  async (addressData, { rejectWithValue }) => {
    try {
      const address = await addressService.createAddress(addressData);
      return address;
    } catch (error) {
      logger.error("Error creating address", error);
      return rejectWithValue(
        error.response?.data?.message || "Failed to create address",
      );
    }
  },
);

/**
 * Update an existing address
 */
export const updateAddress = createAsyncThunk(
  "addresses/updateAddress",
  async ({ addressId, addressData }, { rejectWithValue }) => {
    try {
      const address = await addressService.updateAddress(
        addressId,
        addressData,
      );
      return address;
    } catch (error) {
      logger.error("Error updating address", error);
      return rejectWithValue(
        error.response?.data?.message || "Failed to update address",
      );
    }
  },
);

/**
 * Delete an address
 */
export const deleteAddress = createAsyncThunk(
  "addresses/deleteAddress",
  async (addressId, { rejectWithValue }) => {
    try {
      await addressService.deleteAddress(addressId);
      return addressId;
    } catch (error) {
      logger.error("Error deleting address", error);
      // Handle specific error for linked bookings
      const message =
        error.response?.data?.message || "Failed to delete address";
      return rejectWithValue(message);
    }
  },
);

/**
 * Set an address as default
 */
export const setDefaultAddress = createAsyncThunk(
  "addresses/setDefaultAddress",
  async (addressId, { rejectWithValue }) => {
    try {
      const address = await addressService.setDefaultAddress(addressId);
      return address;
    } catch (error) {
      logger.error("Error setting default address", error);
      return rejectWithValue(
        error.response?.data?.message || "Failed to set default address",
      );
    }
  },
);

// ==================== SLICE ====================

const initialState = {
  addresses: [],
  loading: false,
  error: null,
  // Specific operation states
  createLoading: false,
  createError: null,
  updateLoading: false,
  updateError: null,
  deleteLoading: false,
  deleteError: null,
  setDefaultLoading: false,
  setDefaultError: null,
};

const addressesSlice = createSlice({
  name: "addresses",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCreateError: (state) => {
      state.createError = null;
    },
    clearUpdateError: (state) => {
      state.updateError = null;
    },
    clearDeleteError: (state) => {
      state.deleteError = null;
    },
    clearAllErrors: (state) => {
      state.error = null;
      state.createError = null;
      state.updateError = null;
      state.deleteError = null;
      state.setDefaultError = null;
    },
    // Reset addresses state (e.g., on logout)
    resetAddresses: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Fetch addresses
      .addCase(fetchAddresses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAddresses.fulfilled, (state, action) => {
        state.loading = false;
        state.addresses = action.payload?.addresses || [];
      })
      .addCase(fetchAddresses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create address
      .addCase(createAddress.pending, (state) => {
        state.createLoading = true;
        state.createError = null;
      })
      .addCase(createAddress.fulfilled, (state, action) => {
        state.createLoading = false;
        state.addresses.push(action.payload);
        // If this is the first address or marked as default, update others
        if (action.payload.isDefault) {
          state.addresses = state.addresses.map((addr) =>
            addr.id === action.payload.id
              ? addr
              : { ...addr, isDefault: false },
          );
        }
      })
      .addCase(createAddress.rejected, (state, action) => {
        state.createLoading = false;
        state.createError = action.payload;
      })

      // Update address
      .addCase(updateAddress.pending, (state) => {
        state.updateLoading = true;
        state.updateError = null;
      })
      .addCase(updateAddress.fulfilled, (state, action) => {
        state.updateLoading = false;
        const index = state.addresses.findIndex(
          (addr) => addr.id === action.payload.id,
        );
        if (index !== -1) {
          state.addresses[index] = action.payload;
        }
      })
      .addCase(updateAddress.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = action.payload;
      })

      // Delete address
      .addCase(deleteAddress.pending, (state) => {
        state.deleteLoading = true;
        state.deleteError = null;
      })
      .addCase(deleteAddress.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.addresses = state.addresses.filter(
          (addr) => addr.id !== action.payload,
        );
      })
      .addCase(deleteAddress.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteError = action.payload;
      })

      // Set default address
      .addCase(setDefaultAddress.pending, (state) => {
        state.setDefaultLoading = true;
        state.setDefaultError = null;
      })
      .addCase(setDefaultAddress.fulfilled, (state, action) => {
        state.setDefaultLoading = false;
        // Update all addresses: set the new default and unset others
        state.addresses = state.addresses.map((addr) => ({
          ...addr,
          isDefault: addr.id === action.payload.id,
        }));
      })
      .addCase(setDefaultAddress.rejected, (state, action) => {
        state.setDefaultLoading = false;
        state.setDefaultError = action.payload;
      });

    // Clear addresses on logout
    //   .addCase(logout.fulfilled, () => initialState);
    // TO-DO: For now, don't clear addresses on logout to preserve data until re-login.
    // Post-MVP: Store booking progress + selected address in backend/Redis so users can resume across devices. But for now, localStorage + Redux is fine.
  },
});

// ==================== SELECTORS ====================

export const selectAddresses = (state) => state.addresses?.addresses || [];
export const selectAddressesLoading = (state) =>
  state.addresses?.loading ?? false;
export const selectAddressesError = (state) => state.addresses?.error || null;

export const selectDefaultAddress = (state) =>
  state.addresses?.addresses?.find((addr) => addr.isDefault) || null;

export const selectAddressById = (id) => (state) =>
  state.addresses?.addresses?.find((addr) => addr.id === id) || null;

export const selectCreateLoading = (state) =>
  state.addresses?.createLoading ?? false;
export const selectCreateError = (state) =>
  state.addresses?.createError || null;
export const selectUpdateLoading = (state) =>
  state.addresses?.updateLoading ?? false;
export const selectUpdateError = (state) =>
  state.addresses?.updateError || null;
export const selectDeleteLoading = (state) =>
  state.addresses?.deleteLoading ?? false;
export const selectDeleteError = (state) =>
  state.addresses?.deleteError || null;
export const selectSetDefaultLoading = (state) =>
  state.addresses?.setDefaultLoading ?? false;

// ==================== EXPORTS ====================

export const {
  clearError,
  clearCreateError,
  clearUpdateError,
  clearDeleteError,
  clearAllErrors,
  resetAddresses,
} = addressesSlice.actions;

export default addressesSlice.reducer;
